import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '@/components/ui/dialog';
import { Plus, X } from 'lucide-react';
import { saveMarketingLink } from '@/lib/api';
import { toast } from 'sonner';

/**
 * MarketingLinkEditDialog — reusable dialog to edit/create marketing links for a unit.
 */
export function MarketingLinkEditDialog({ open, onOpenChange, unit, existing, onSaved }) {
  const [form, setForm] = useState({
    airbnb_link: '',
    furnished_finder_link: '',
    photos_link: '',
    additional_links: [],
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) {
      setForm({
        airbnb_link: existing?.airbnb_link || '',
        furnished_finder_link: existing?.furnished_finder_link || '',
        photos_link: existing?.photos_link || '',
        additional_links: existing?.additional_links || [],
      });
    }
  }, [open, existing]);

  const addLink = () => setForm(f => ({ ...f, additional_links: [...f.additional_links, { name: '', url: '' }] }));
  const updateLink = (idx, field, val) => setForm(f => {
    const arr = [...f.additional_links];
    arr[idx] = { ...arr[idx], [field]: val };
    return { ...f, additional_links: arr };
  });
  const removeLink = (idx) => setForm(f => ({ ...f, additional_links: f.additional_links.filter((_, i) => i !== idx) }));

  const handleSave = async () => {
    if (!unit) return;
    setSaving(true);
    try {
      await saveMarketingLink({ unit_id: unit.id, property_id: unit.property_id, ...form });
      toast.success('Links saved');
      onSaved?.();
      onOpenChange(false);
    } catch {
      toast.error('Failed to save');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Marketing Links — Unit {unit?.unit_number}</DialogTitle>
          <DialogDescription>Add listing links and photo storage for this unit.</DialogDescription>
        </DialogHeader>
        <div className="grid gap-4 py-3">
          <div className="space-y-2">
            <Label>Airbnb Listing URL</Label>
            <Input
              value={form.airbnb_link}
              onChange={e => setForm(f => ({ ...f, airbnb_link: e.target.value }))}
              placeholder="https://airbnb.com/rooms/..."
              data-testid="marketing-airbnb-input"
            />
          </div>
          <div className="space-y-2">
            <Label>Furnished Finder URL</Label>
            <Input
              value={form.furnished_finder_link}
              onChange={e => setForm(f => ({ ...f, furnished_finder_link: e.target.value }))}
              placeholder="https://furnishedfinder.com/..."
              data-testid="marketing-ff-input"
            />
          </div>
          <div className="space-y-2">
            <Label>Photos Storage URL</Label>
            <Input
              value={form.photos_link}
              onChange={e => setForm(f => ({ ...f, photos_link: e.target.value }))}
              placeholder="https://drive.google.com/..."
            />
          </div>
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <Label>Additional Links</Label>
              <Button variant="secondary" size="sm" onClick={addLink} data-testid="marketing-add-link-btn">
                <Plus className="h-3 w-3 mr-1" />Add Link
              </Button>
            </div>
            {form.additional_links.map((al, idx) => (
              <div key={idx} className="flex gap-2 items-center">
                <Input className="w-1/3" placeholder="Label" value={al.name} onChange={e => updateLink(idx, 'name', e.target.value)} />
                <Input className="flex-1" placeholder="URL" value={al.url} onChange={e => updateLink(idx, 'url', e.target.value)} />
                <Button variant="ghost" size="sm" className="h-8 w-8 p-0 text-destructive" onClick={() => removeLink(idx)}>
                  <X className="h-4 w-4" />
                </Button>
              </div>
            ))}
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={handleSave} disabled={saving} data-testid="marketing-save-btn">
            {saving ? 'Saving...' : 'Save Links'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
